import type { ComponentProps, FunctionComponent, PropsWithChildren } from 'react'
import {
  TouchableWithoutFeedback,
  View,
  ScrollView,
  type ViewStyle,
  type TextStyle,
  Platform,
  Text,
  StyleSheet,
} from 'react-native'
import { ChevronRight } from '@/src/assets/icons/ChevronRight'
import { mesaColorNames, spacing, typography } from '@/src/common/theme'
import { ListItem } from 'tamagui'
import { isE2E } from '@/src/common/environment'
import { formatNumber } from '@/src/common/utils/general'
import { Link } from 'expo-router'
import { BoxedIcon } from '@/src/design-system/BoxedIcon'
import { Skeleton } from '@/src/design-system/Skeleton'

const gap = 8 satisfies ViewStyle['gap']

const oneLineTextPaddingBottom = (
  Platform.OS === 'web'
    ? // without this, the text characters' descenders will be cut off
      3
    : 0
) satisfies TextStyle['paddingBottom']

function OneLineText(props: ComponentProps<typeof Text>) {
  return (
    <Text
      numberOfLines={1}
      {...props}
      style={[
        { paddingBottom: oneLineTextPaddingBottom },
        'style' in props ? props.style : undefined,
      ]}
    />
  )
}

export const Item = ({
  onPress,
  icon,
  title: nullableTitle,
  details,
  date: nullableDate,
  amount,
  amountColor,
  status,
  loading,
  disabled,
  points,
  testID = '',
  linkProps,
  style,
}: Pick<ComponentProps<typeof TouchableWithoutFeedback>, 'onPress' | 'disabled'> &
  Pick<ComponentProps<typeof ListItem>, 'style'> & {
    icon?: React.ReactElement
    title?: string | null
    details?: string | null
    date?: PropsWithChildren['children']
    amount?: PropsWithChildren['children']
    amountColor?: string
    status?: string | null
    loading?: boolean
    points?: number
    testID?: string
    linkProps?: ComponentProps<typeof Link> & {
      As?: FunctionComponent<ComponentProps<typeof Link>>
    }
  }) => {
  const title = nullableTitle || 'N/A'
  const date = nullableDate || 'Date unavailable'

  const amountElement = (moreStyles?: TextStyle) => (
    <OneLineText
      style={[
        styles.amount,
        { color: amountColor, flexShrink: 0 },
        ...(moreStyles ? [moreStyles] : []),
      ]}
    >
      {amount}
    </OneLineText>
  )
  const somethingIsBeingDisplayedUnderneathTheAmount = status || details

  const listItem = (
    <ListItem
      tag="a"
      hoverTheme
      pressTheme
      testID={testID}
      disabled={disabled || (!onPress && !linkProps?.href)}
      disabledStyle={{ opacity: 1 }}
      onPress={onPress}
      // @ts-expect-error tamagui typescript issue?
      pressStyle={{ opacity: 0.2, backgroundColor: 'transparent' }}
      animation="75ms"
      // @ts-expect-error tamagui typescript issue?
      hoverStyle={{ backgroundColor: `${mesaColorNames['semantic/fg/subtle #898D8F']}20` }}
      backgroundColor="transparent"
      style={StyleSheet.flatten([
        {
          textDecorationLine: 'none',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap,
          paddingLeft: spacing['4'],
          paddingRight: spacing['4'],
          paddingTop: 10,
          paddingBottom: 10,
        },
        StyleSheet.flatten(style),
      ])}
    >
      {loading && !isE2E ? (
        <Skeleton style={{ height: 36, width: 36 }} />
      ) : icon ? (
        <BoxedIcon icon={icon} />
      ) : (
        <View style={{ height: 36, width: 36 }} />
      )}
      {loading && !isE2E ? (
        <Skeleton style={{ height: 36, flex: 1 }} />
      ) : (
        <View
          style={{
            flex: 1,
            gap,
            flexDirection: 'row',
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
          }}
        >
          <View
            style={{
              flex: 1,
              gap,
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'stretch',
            }}
          >
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <OneLineText
                style={[styles.title, { flexShrink: 1 }, !nullableTitle && { fontStyle: 'italic' }]}
              >
                {title}
              </OneLineText>
              {somethingIsBeingDisplayedUnderneathTheAmount && amountElement({ marginLeft: 8 })}
            </View>
            {date ? (
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <OneLineText style={[styles.subtext, { flexShrink: 0 }]}>{date}</OneLineText>
                <View style={{ flexShrink: 1, flexDirection: 'row' }}>
                  {details && (
                    <ScrollView style={{ marginLeft: 8, marginRight: status ? 8 : 0 }} horizontal>
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                        }}
                        onStartShouldSetResponder={() => true}
                      >
                        <OneLineText style={styles.subtext}>{details}</OneLineText>
                      </View>
                    </ScrollView>
                  )}
                  {status && (
                    <OneLineText style={[styles.subtext, { flexShrink: 0 }]}>{status}</OneLineText>
                  )}
                </View>
              </View>
            ) : null}
          </View>

          {!somethingIsBeingDisplayedUnderneathTheAmount && (
            <View
              style={{
                gap,
                flexDirection: 'column',
                alignItems: 'flex-end',
                justifyContent: 'center',
              }}
            >
              {amountElement()}
              {points ? (
                <OneLineText style={[styles.subtext, { flexShrink: 0 }]}>
                  {formatNumber(points)} Points
                </OneLineText>
              ) : null}
            </View>
          )}
          <View
            style={{
              flexShrink: 0,
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'stretch',
              width: 18,
            }}
          >
            {onPress || linkProps?.href ? (
              <ChevronRight color={mesaColorNames['Greyscale/500 #6E7375']} />
            ) : null}
          </View>
        </View>
      )}
    </ListItem>
  )

  if (!linkProps?.href) return listItem

  const LinkComponent = linkProps.As || Link

  return (
    <LinkComponent asChild {...linkProps}>
      {listItem}
    </LinkComponent>
  )
}

const styles = StyleSheet.create({
  amount: StyleSheet.flatten([
    typography['label/sm-bold'],
    { color: mesaColorNames['Primary/Brand #1C1C1C'] },
  ]),
  title: StyleSheet.flatten([
    typography['label/sm-regular'],
    { color: mesaColorNames['global/grey/80 #2F3133'] },
  ]),
  subtext: StyleSheet.flatten([
    typography['label/xs-regular'],
    { color: mesaColorNames['Greyscale/500 #6E7375'] },
  ]),
})
